import { AsyncPipe, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RecipesTranslationFormComponent } from '@recipes/recipes-translation/recipes-translation-form/recipes-translation-form.component';
import { NotificationService } from '@services/notification.service';
import { RecipeService } from '@services/recipe/recipe.service';
import { RefDataService } from '@services/ref-data.service';
import { LocalizedKeyValue } from '@shared/models/dataref.model';
import { EditRecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { TranslatedVariants } from '@shared/models/recipe/recipes-translation-form.model';
import { concatAll, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-recipes-translation',
  templateUrl: './recipes-translation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RecipesTranslationFormComponent, NgIf, AsyncPipe],
})
export class RecipesTranslationComponent implements OnInit {
  translatedVariantsData$: Observable<{
    translatedVariants: TranslatedVariants;
    stepTypes: Record<string, LocalizedKeyValue>;
  }>;
  identifier: EditRecipeLite['identifier'];

  constructor(
    private readonly recipeService: RecipeService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly refDataService: RefDataService,
    private readonly notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    const topRecipeId = this.route.snapshot.paramMap.get('topRecipeId');

    if (!topRecipeId) {
      this.router.navigate(['404']).then(() => {
        console.error('Missing "topRecipeId" parameter in route.');
      });
      return;
    }

    this.translatedVariantsData$ = this.recipeService.getTranslationEditDetails(topRecipeId).pipe(
      catchError((e: HttpErrorResponse) => {
        if (e.status === 404) {
          this.router.navigate(['404']).then(() => {
            console.error(e.message);
          });
        }

        return of(undefined);
      }),
      tap((translatedVariants) => {
        if (!translatedVariants || Object.keys(translatedVariants).length === 0) {
          this.router.navigate(['404']).then(() => {
            console.error('No translated variants.');
          });
        }
      }),
      filter((translatedVariants): translatedVariants is TranslatedVariants => !!translatedVariants),
      tap((translatedVariants) => {
        this.identifier = translatedVariants.targetRecipes[0].identifier;
      }),
      switchMap((translatedVariants: TranslatedVariants) => {
        const { domain, market, lang } = translatedVariants.sourceRecipes[0];

        return this.refDataService.getByKey('stepTypes', false, domain, market, lang).pipe(
          map((stepTypes: Record<string, LocalizedKeyValue>) => ({
            translatedVariants,
            stepTypes,
          })),
        );
      }),
    );
  }

  onTranslationUpdated({ recipes, isApproved }: { recipes: EditRecipeLite[]; isApproved: boolean }): void {
    let hasThrowHttpError = false;

    from(recipes)
      .pipe(
        map((recipe) =>
          this.recipeService.updateRecipe(recipe).pipe(
            catchError((err) => {
              hasThrowHttpError = true;
              console.error(`Error while trying to edit recipe, identifier: ${recipe.identifier.functionalId}`, err);

              return of(undefined);
            }),
          ),
        ),
        concatAll(),
        finalize(() => {
          if (hasThrowHttpError) {
            this.notificationService.sendError('RECIPE_TRANSLATION.MSG.SAVE.ERROR');
            return;
          }

          if (isApproved) {
            this.notificationService.sendSuccess('RECIPE_TRANSLATION.MSG.SAVE.APPROVED');
            const { sourceSystem, functionalId } = this.identifier;

            this.router.navigate(['recipes', 'edit', sourceSystem, functionalId]).catch((err) => {
              console.error(`Error while navigating to: recipes/edit/${sourceSystem}/${functionalId}`, err);
            });
            return;
          }
          this.notificationService.sendSuccess('RECIPE_TRANSLATION.MSG.SAVE.SUCCESS');
        }),
      )
      .subscribe();
  }
}
